<template>
  <div class="analysis-of-business app-page app-page--fill">
    <div class="main-content">
      <!-- 左侧：经营项目列表 -->
      <div class="left-panel">
        <el-card shadow="hover" class="list-card">
          <template #header>
            <PageHeader size="card">
              <template #title>项目列表</template>
              <template #actions>
                <el-button 
                  v-if="can('列表')"
                  type="primary" 
                  size="small" 
                  class="app-refresh-btn"
                  :icon="Refresh" 
                  :loading="leftLoading"
                  @click="loadBusinessProjects"
                  title="刷新"
                  circle
                />
              </template>
            </PageHeader>
          </template>

          <div class="business-list-container">
            <DynamicTable
              ref="dynamicTableRef"
              :data="businessProjects"
              :model="tableModel"
              :loading="leftLoading"
              :show-column-selector="true"
              :show-export="false"
              :show-detail-button="can('详情')"
              :show-edit-button="false"
              :show-delete-button="false"
              :operation-no-wrap="true"
              :pagination="{
                show: true,
                page: leftPagination.currentPage,
                pageSize: leftPagination.pageSize,
                total: leftPagination.total,
                pageSizes: [10, 20, 50],
                layout: 'total, sizes, prev, pager, next',
                small: true
              }"
              @detail="handleBusinessDetail"
              @filter="handleFilter"
              @sort="handleSort"
              @page-change="handleLeftPageChange"
              @page-size-change="handleLeftSizeChange"
            >
              <!-- 自定义操作列 -->
              <template #action="{ row }">
                <el-button
                  v-if="can('打印')"
                  type="warning"
                  size="small"
                  :icon="Printer"
                  @click="showPrintDialog(row)"
                >
                  原始记录打印
                </el-button>
              </template>
            </DynamicTable>
          </div>
        </el-card>
      </div>


      <!-- 右侧：检测数据列表和分析区域 -->
      <div class="right-panel">
        <!-- 上方：原始记录列表 -->
        <el-card shadow="hover" class="detection-list-card">
          <template #header>
            <PageHeader size="card">
              <template #title>原始记录列表</template>
              <template #actions>
                <el-tag v-if="currentBusinessName" type="success" size="small">
                  {{ currentBusinessName }}
                </el-tag>
                <el-button
                  v-if="selectedBusinessId && can('列表')"
                  type="primary"
                  size="small"
                  class="app-refresh-btn"
                  :icon="Refresh"
                  :loading="rightLoading"
                  @click="refreshDetectionData"
                  title="刷新"
                  circle
                />
              </template>
            </PageHeader>
          </template>

          <div v-loading="rightLoading" class="detection-list-container">
            <el-scrollbar class="detection-scrollbar">
              <div 
                v-for="item in detectionDataList" 
                :key="item.Id"
                class="detection-item"
              >
                <div class="detection-item-header">
                  <div class="detection-info">
                    <h4>名称：{{ item.名称 || '未命名检测数据' }}</h4>
                    <div class="detection-meta">
                      <el-tag size="small">编码：{{ item.编码 || '无编码' }}</el-tag>
                      <el-tag v-if="item.提交人员" size="small" type="warning">
                        提交人员：{{ item.提交人员 }}
                      </el-tag>
                      <span class="detection-time">开始：{{ formatDateTime(item.开始时间) }}</span>
                      <span v-if="item.完成时间" class="detection-time">
                        完成：{{ formatDateTime(item.完成时间) }}
                      </span>
                    </div>
                  </div>
                  <div class="detection-actions">
                    <el-button 
                      v-if="can('设置人员')"
                      :type="isStaffSet(item) ? 'success' : 'danger'" 
                      size="small"
                      @click="openStaffDialog(item)"
                    >
                      {{ isStaffSet(item) ? '已设置人员' : '请设置人员' }}
                    </el-button>
                    <el-button 
                      v-if="can('详情')"
                      type="primary" 
                      size="small"
                      @click="viewDetectionDetail(item)"
                    >
                      详情
                    </el-button>
                  </div>
                </div>

                <!-- 检测项目统计 -->
                <div v-if="item.检测原始数据检测项目集合 && item.检测原始数据检测项目集合.length > 0" class="detection-projects">
                  <div class="project-stats">
                    <el-tag 
                      v-for="project in item.检测原始数据检测项目集合.slice(0, 3)" 
                      :key="project.Id"
                      size="small"
                      type="info"
                      class="project-tag"
                    >
                      {{ project.名称 }}
                      <span v-if="project.数量">({{ project.数量 }})</span>
                    </el-tag>
                    <el-tag 
                      v-if="item.检测原始数据检测项目集合.length > 3"
                      size="small"
                      type="info"
                    >
                      +{{ item.检测原始数据检测项目集合.length - 3 }}
                    </el-tag>
                  </div>
                </div>
              </div>

              <el-empty 
                v-if="!rightLoading && detectionDataList.length === 0" 
                description="请选择经营项目查看检测数据"
              />
            </el-scrollbar>

            <div v-if="showBusinessActionArea" class="business-action-bar business-action-bar--inside">
              <el-button
                v-if="showWithdrawButton && can('撤回项目')"
                type="warning"
                :loading="businessActionLoading"
                @click="handleBusinessWithdraw"
              >
                {{ BUSINESS_WITHDRAW_TEXT }}
              </el-button>
              <el-button
                v-if="showCompleteButton && can('完成项目')"
                type="primary"
                :loading="businessActionLoading"
                @click="handleBusinessComplete"
              >
                {{ BUSINESS_COMPLETE_TEXT }}
              </el-button>
            </div>
          </div>
        </el-card>

        <el-card shadow="hover" class="analysis-card">
          <template #header>
            <PageHeader size="card">
              <template #title>数据分析汇总</template>
              <template #actions>
                <el-button
                  v-if="selectedBusinessId && hasDetectionData && can('汇总查询')"
                  type="primary"
                  size="small"
                  :icon="DataAnalysis"
                  @click="viewBusinessDataSummary"
                >
                  数据汇总
                </el-button>
              </template>
            </PageHeader>
          </template>

          <div class="analysis-container">
            <!-- 筛选条件区域 -->
            <div v-if="selectedBusinessId && hasDetectionData" class="analysis-filters">
              <!-- 第一行：下拉选择框 -->
              <div class="filter-row">
                <span class="filter-label">过滤条件：</span>
                <el-select 
                  v-model="analysisFilters.建筑名称" 
                  placeholder="建筑" 
                  clearable 
                  size="small"
                  @change="handleBuildingFilterChange"
                >
                  <el-option 
                    v-for="item in filterOptions.建筑名称" 
                    :key="item" 
                    :label="item" 
                    :value="item" 
                  />
                </el-select>
                <el-select 
                  v-if="analysisFilters.建筑名称"
                  v-model="analysisFilters.层数" 
                  placeholder="层数" 
                  clearable 
                  size="small"
                  class="floor-select"
                  @change="handleAnalysisFilterChange"
                >
                  <el-option 
                    v-for="item in currentBuildingFloors" 
                    :key="item" 
                    :label="`${item}层`" 
                    :value="item" 
                  />
                </el-select>
                <el-select 
                  v-model="analysisFilters.检测人员" 
                  placeholder="检测人员" 
                  clearable 
                  size="small"
                  @change="handleAnalysisFilterChange"
                >
                  <el-option 
                    v-for="item in filterOptions.检测人员" 
                    :key="item" 
                    :label="item" 
                    :value="item" 
                  />
                </el-select>
                <el-select 
                  v-model="analysisFilters.检测对象名称" 
                  placeholder="检测对象" 
                  clearable 
                  size="small"
                  @change="handleAnalysisFilterChange"
                >
                  <el-option 
                    v-for="item in filterOptions.检测对象名称" 
                    :key="item" 
                    :label="item" 
                    :value="item" 
                  />
                </el-select>
                <el-select 
                  v-model="analysisFilters.检测项目名称" 
                  placeholder="检测项目" 
                  clearable 
                  size="small"
                  @change="handleAnalysisFilterChange"
                >
                  <el-option 
                    v-for="item in filterOptions.检测项目名称" 
                    :key="item" 
                    :label="item" 
                    :value="item" 
                  />
                </el-select>
                <el-date-picker
                  v-model="analysisFilters.开始时间"
                  type="datetime"
                  value-format="YYYY-MM-DD HH:mm:ss"
                  placeholder="开始时间"
                  clearable
                  size="small"
                  class="analysis-time-picker"
                  @change="handleAnalysisFilterChange"
                />
                <el-date-picker
                  v-model="analysisFilters.结束时间"
                  type="datetime"
                  value-format="YYYY-MM-DD HH:mm:ss"
                  placeholder="结束时间"
                  clearable
                  size="small"
                  class="analysis-time-picker"
                  @change="handleAnalysisFilterChange"
                />
                <el-button size="small" @click="setAnalysisDateRange('today')">今天</el-button>
                <el-button size="small" @click="setAnalysisDateRange('yesterday')">昨天</el-button>
              </div>
              
              <!-- 第二行：复选框（至少选中一项） -->
              <div class="filter-row radio-row">
                <span class="filter-label">分组条件：</span>
                <el-checkbox v-model="analysisFilters.人员" @change="handleGroupConditionChange('人员')">人员</el-checkbox>
                <el-checkbox v-model="analysisFilters.检测项目" @change="handleGroupConditionChange('检测项目')">检测项目</el-checkbox>
                <el-checkbox v-model="analysisFilters.检测对象" @change="handleGroupConditionChange('检测对象')">检测对象</el-checkbox>
                <el-checkbox v-model="analysisFilters.建筑" @change="handleGroupConditionChange('建筑')">建筑</el-checkbox>
                <el-checkbox v-model="analysisFilters.楼层" @change="handleGroupConditionChange('楼层')">楼层</el-checkbox>
                <span class="total-count">合计：<strong>{{ totalCount }}</strong></span>
              </div>
              
              <!-- 分析结果表格 -->
              <div v-loading="analysisLoading" class="analysis-result">
                <el-table :data="analysisResult" stripe size="small" class="analysis-table" height="100%">
                  <el-table-column label="分组条件" min-width="200">
                    <template #default="{ row }">
                      <div class="group-info">
                        <div 
                          v-for="(value, key) in getFormattedGroupCondition(row.分组条件)" 
                          :key="key"
                          class="group-item"
                        >
                          <span class="group-label">{{ key }}:</span>
                          <span class="group-value">{{ value }}</span>
                        </div>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="数量" width="80" align="center">
                    <template #default="{ row }">
                      <el-tag type="primary" size="small">{{ row.数量 }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column v-if="can('项目进度分析')" label="操作" width="100" align="center">
                    <template #default="{ row }">
                      <el-button 
                        type="primary" 
                        size="small" 
                        @click="showDetailDialog(row)"
                      >
                        查看详情
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>

            <el-empty v-else-if="selectedBusinessId" :image-size="120" description="当前经营项目暂无检测数据，无法生成数据分析汇总">
              <template #image>
                <el-icon size="48" color="#909399">
                  <DataAnalysis />
                </el-icon>
              </template>
            </el-empty>
            
            <el-empty v-else :image-size="120" description="请选择经营项目查看数据分析汇总">
              <template #image>
                <el-icon size="48" color="#909399">
                  <DataAnalysis />
                </el-icon>
              </template>
            </el-empty>
          </div>
        </el-card>
      </div>
    </div>

    <!-- 设置人员弹框 -->
    <el-dialog
      v-model="staffDialogVisible"
      title="设置人员"
      width="450px"
      :close-on-click-modal="false"
    >
      <el-form :model="staffForm" label-width="80px">
        <el-form-item label="检验人员">
          <el-input v-model="staffForm.检验人员" placeholder="请输入检验人员" />
        </el-form-item>
        <el-form-item label="校核人员">
          <el-input v-model="staffForm.校核人员" placeholder="请输入校核人员" />
        </el-form-item>
        <el-form-item label="计算人员">
          <el-input v-model="staffForm.计算人员" placeholder="请输入计算人员" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="staffDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSetStaff" :loading="staffLoading">
          确定
        </el-button>
      </template>
    </el-dialog>
    <!-- 详情弹框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="详情信息"
      width="min(1100px, 92vw)"
      top="5vh"
      :close-on-click-modal="false"
      class="analysis-detail-dialog"
      @close="clearAnalysisRemarkImages"
    >
      <div v-if="currentDetailRow" class="detail-dialog-content">
        <div class="detail-header">
          <div class="detail-title">{{ currentDetailRow.分组条件?.数据名称 }}</div>
          <div class="detail-header-actions">
            <el-tag type="primary">数量: {{ currentDetailRow.数量 }}</el-tag>
            <el-button
              type="primary"
              size="small"
              :loading="detailExpandLoading"
              @click="toggleAllAnalysisDetails"
            >
              {{ allDetailsExpanded ? '一键收起' : '一键展开' }}
            </el-button>
          </div>
        </div>
        <div class="detail-list">
          <div
            v-for="item in analysisDetailItems"
            :key="item.key"
            class="detail-item"
            :class="{ 'detail-item--expanded': item.expanded }"
          >
            <div class="detail-item-summary">
              <div class="detail-item-text">{{ item.summary || '无摘要' }}</div>
              <div class="detail-item-actions">
                <span v-if="item.id" class="detail-item-id">Id: {{ item.id }}</span>
                <el-button
                  link
                  type="primary"
                  size="small"
                  :loading="item.loading"
                  @click="toggleAnalysisDetail(item)"
                >
                  {{ item.expanded ? '收起' : '展开' }}
                </el-button>
              </div>
            </div>

            <div v-if="item.expanded" class="embedded-detail">
              <el-alert
                v-if="item.error"
                :title="item.error"
                type="error"
                :closable="false"
                show-icon
              />
              <el-skeleton v-else-if="item.loading" :rows="4" animated />
              <template v-else-if="item.data">
                <div
                  v-if="getAnalysisBasicInfo(item.data).length > 0"
                  class="embedded-basic-info"
                >
                  <el-tag
                    v-for="field in getAnalysisBasicInfo(item.data)"
                    :key="field.key"
                    type="info"
                    size="small"
                    class="embedded-info-tag"
                    :title="`${field.key}：${field.value}`"
                  >
                    <span class="embedded-info-tag-label">{{ field.key }}：</span>
                    <span class="embedded-info-tag-value">{{ field.value }}</span>
                  </el-tag>
                </div>

                <div
                  v-if="getAnalysisProjects(item.data).length > 0"
                  class="embedded-project-list"
                >
                  <div
                    v-for="(project, projectIndex) in getAnalysisProjects(item.data)"
                    :key="String(project.Id || project.id || projectIndex)"
                    class="embedded-project"
                  >
                    <div class="embedded-section-title">
                      {{ project.名称 || `检测项目 ${Number(projectIndex) + 1}` }}
                    </div>

                    <div
                      v-if="getAnalysisSpecialAttributes(project).length > 0"
                      class="embedded-attributes"
                    >
                      <el-tag
                        v-for="attribute in getAnalysisSpecialAttributes(project)"
                        :key="String(attribute.Id || attribute.id || attribute.标题)"
                        type="success"
                        size="small"
                      >
                        {{ attribute.标题 }}：{{ formatAnalysisDisplayValue(attribute.内容) }}
                      </el-tag>
                    </div>

                    <div
                      v-for="(mainData, mainIndex) in getAnalysisMainTables(project)"
                      :key="String(mainData.Id || mainData.id || mainIndex)"
                      class="embedded-main-table"
                    >
                      <div class="embedded-table-title">
                        数据表 {{ Number(mainIndex) + 1 }}
                        <span v-if="mainData.显示摘要">{{ mainData.显示摘要 }}</span>
                      </div>
                      <div
                        v-if="getAnalysisSingleAttributes(mainData).length > 0"
                        class="embedded-attributes"
                      >
                        <el-tag
                          v-for="attribute in getAnalysisSingleAttributes(mainData)"
                          :key="String(attribute.Id || attribute.id || attribute.标题)"
                          type="success"
                          size="small"
                        >
                          {{ attribute.标题 }}：{{ formatAnalysisDisplayValue(attribute.内容) }}
                        </el-tag>
                      </div>
                      <el-table
                        v-if="getAnalysisSubRows(mainData).length > 0"
                        :data="getAnalysisSubRows(mainData)"
                        border
                        size="small"
                        max-height="360"
                        class="embedded-data-table"
                      >
                        <el-table-column
                          v-for="column in getAnalysisTableColumns(project, mainData)"
                          :key="column.prop"
                          :prop="column.prop"
                          :label="column.label"
                          min-width="110"
                          show-overflow-tooltip
                        />
                      </el-table>
                      <div
                        v-if="getAnalysisImages(mainData).length > 0"
                        class="embedded-images"
                      >
                        <span class="embedded-images-label">现场照片：</span>
                        <div
                          v-for="(image, imageIndex) in getAnalysisImages(mainData)"
                          :key="`${image.fileName}-${imageIndex}`"
                          class="embedded-image-item"
                        >
                          <el-image
                            v-if="image.url"
                            :src="image.url"
                            fit="cover"
                            class="embedded-image-thumb"
                            :preview-src-list="getAnalysisImages(mainData).filter((img) => img.url).map((img) => img.url)"
                            :initial-index="getAnalysisImageInitialIndex(mainData, imageIndex)"
                            :preview-teleported="true"
                            hide-on-click-modal
                            :title="image.fileName"
                          />
                          <div
                            v-else
                            class="embedded-image-placeholder"
                            :class="{ 'is-error': image.error }"
                          >
                            {{ image.loading ? '加载中…' : '加载失败' }}
                          </div>
                          <div
                            v-if="image.remark"
                            class="embedded-image-remark"
                            :title="image.remark"
                          >
                            {{ image.remark }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <pre
                  v-if="getAnalysisProjects(item.data).length === 0"
                  class="embedded-json"
                >{{ formatAnalysisJson(item.data) }}</pre>
              </template>
            </div>
          </div>
          <el-empty v-if="analysisDetailItems.length === 0" description="暂无详情数据" />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh, DataAnalysis, Printer } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import ParameterSettings from '@/network/apis/ParameterSettings'
import { list } from '@/network/index'
import PageHeader from '@/components/PageHeader.vue'
import { useDetailStore } from '@/store/detailStore'
import DynamicTable from '@/components/DynamicTable.vue'
import { getMenuChildPathByComponent, getMenuPathByComponent } from '@/utils/menuRoute'
import { useRouteTitle } from '@/utils/useRouteTitle'
import { useButtonPermission } from '@/utils/buttonPermission'

const { can } = useButtonPermission('/RawDataManagement/AnalysisOfBusiness/index.vue')

type BusinessActionApi = {
  analysisOfBusinessWithdraw: (params: { id: number | string }) => Promise<any>
  analysisOfBusinessComplete: (params: { id: number | string }) => Promise<any>
}

const router = useRouter()
const detailStore = useDetailStore()
useRouteTitle('经营项目数据分析')
const businessActionApi = ParameterSettings as typeof ParameterSettings & BusinessActionApi

// 左侧面板数据
const leftLoading = ref(false)
const businessProjects = ref<any[]>([])
const selectedBusinessId = ref<number | null>(null)
const currentBusinessName = ref<string>('')
const tableModel = ref<Record<string, string> | undefined>(undefined)

// 左侧分页
const leftPagination = ref({
  currentPage: 1,
  pageSize: 10,
  total: 0
})

// 筛选和排序参数
const filterParams = ref<any[]>([])

// 右侧面板数据
const rightLoading = ref(false)
const detectionDataList = ref<any[]>([])
const currentBusinessDetail = ref<any>(null)
const currentBusinessEditable = ref(false)
const businessDetailClickedId = ref<number | string | null>(null)
const businessActionLoading = ref(false)

const BUSINESS_WITHDRAW_TEXT = '经营项目撤回'
const BUSINESS_COMPLETE_TEXT = '项目管理完成'
const STATUS_EXECUTION_COMPLETED = '执行完成'
const STATUS_CONFIG_EXECUTING = '配置完成，执行中'

// 设置人员弹框相关
const staffDialogVisible = ref(false)
const staffLoading = ref(false)
const currentStaffItemId = ref<number | string | null>(null)
const staffForm = ref({
  检验人员: '',
  校核人员: '',
  计算人员: ''
})

// 建筑和层数选择
const selectedBuildingId = ref<string | null>(null)
const selectedFloor = ref<number | null>(null)

// 建筑列表（从当前经营项目详情中提取）
const buildingList = computed(() => {
  if (!currentBusinessDetail.value || !currentBusinessDetail.value['建筑列表']) {
    return []
  }
  return currentBusinessDetail.value['建筑列表'] as Array<Record<string, unknown>>
})

// 当前选中建筑的层数选项
const currentBuildingFloors = computed(() => {
  if (!analysisFilters.value.建筑名称 || !currentBusinessDetail.value) return []
  
  const buildingList = currentBusinessDetail.value['建筑列表'] as Array<Record<string, unknown>>
  if (!buildingList) return []
  
  const building = buildingList.find(b => b['名称'] === analysisFilters.value.建筑名称)
  if (!building) return []
  
  const floorCount = parseInt(String(building['层数'] || 0))
  if (floorCount <= 0) return []
  
  // 生成 1 到 floorCount 的数组
  return Array.from({ length: floorCount }, (_, i) => String(i + 1))
})

// 建筑选择变化时重置层数并触发筛选
const handleBuildingFilterChange = () => {
  analysisFilters.value.层数 = ''
  handleAnalysisFilterChange()
}

// 数据分析汇总相关
const analysisLoading = ref(false)
const filterOptions = ref({
  层数: [] as string[],
  检测人员: [] as string[],
  检测对象名称: [] as string[],
  检测项目名称: [] as string[],
  建筑名称: [] as string[]
})
const analysisFilters = ref({
  层数: '',
  检测人员: '',
  检测对象名称: '',
  检测项目名称: '',
  建筑名称: '',
  开始时间: '',
  结束时间: '',
  人员: false,
  检测项目: true, // 默认选中检测项目
  检测对象: false,
  建筑: false,
  楼层: false
})
const analysisResult = ref<any[]>([])

// 计算总数量
const totalCount = computed(() => {
  return analysisResult.value.reduce((sum, item) => sum + (Number(item.数量) || 0), 0)
})

// 详情弹框相关
const detailDialogVisible = ref(false)
const currentDetailRow = ref<any>(null)

interface AnalysisDetailItem {
  key: string
  raw: unknown
  summary: string
  id: string
  expanded: boolean
  loading: boolean
  error: string
  data: any
}

const analysisDetailItems = ref<AnalysisDetailItem[]>([])
const detailExpandLoading = ref(false)

const allDetailsExpanded = computed(() => {
  return analysisDetailItems.value.length > 0 && analysisDetailItems.value.every(item => item.expanded)
})

const parseAnalysisDetailValue = (value: unknown): any => {
  if (typeof value !== 'string') return value
  const text = value.trim()
  if (!text) return value
  try {
    return JSON.parse(text)
  } catch {
    return value
  }
}

const getAnalysisDetailItems = (row: any): AnalysisDetailItem[] => {
  const details = Array.isArray(row?.详情) ? row.详情 : []
  return details.map((raw: unknown, index: number) => {
    const parsed = parseAnalysisDetailValue(raw)
    const objectValue = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    const summary = typeof parsed === 'string'
      ? parsed
      : String(objectValue.摘要 ?? objectValue.summary ?? objectValue.名称 ?? '')
    const idValue = objectValue.Id ?? objectValue.id
    return {
      key: String(idValue ?? index),
      raw,
      summary,
      id: idValue === undefined || idValue === null ? '' : String(idValue),
      expanded: false,
      loading: false,
      error: '',
      data: null
    }
  })
}

const getAnalysisResponseData = (response: any): any => {
  if (!response) return null
  if (response.success === false) return null
  if (response.response !== undefined) return response.response
  if (response.data !== undefined) return response.data
  return response
}

const loadAnalysisDetail = async (item: AnalysisDetailItem): Promise<boolean> => {
  if (item.data) return true
  if (!item.id) {
    item.error = '当前记录缺少Id，无法加载详情'
    return false
  }

  item.loading = true
  item.error = ''
  try {
    const response = await ParameterSettings.analysisBusinessDetail({ id: item.id })
    const data = getAnalysisResponseData(response)
    if (!data) {
      item.error = response?.msg || '详情加载失败'
      return false
    }
    item.data = data
    return true
  } catch (error) {
    console.error('加载经营数据分析详情失败:', error)
    item.error = '详情加载失败，请稍后重试'
    return false
  } finally {
    item.loading = false
  }
}

const toggleAnalysisDetail = async (item: AnalysisDetailItem) => {
  if (item.expanded) {
    item.expanded = false
    return
  }
  const loaded = await loadAnalysisDetail(item)
  item.expanded = loaded || !!item.error
  if (item.expanded && item.data) {
    // 展开成功后加载现场照片（已加载过会自动跳过）
    loadAllAnalysisRemarkImages(item.data)
  }
}

const toggleAllAnalysisDetails = async () => {
  if (allDetailsExpanded.value) {
    analysisDetailItems.value.forEach(item => { item.expanded = false })
    return
  }

  detailExpandLoading.value = true
  try {
    await Promise.all(analysisDetailItems.value.map(async item => {
      const loaded = await loadAnalysisDetail(item)
      item.expanded = loaded || !!item.error
      if (item.expanded && item.data) {
        loadAllAnalysisRemarkImages(item.data)
      }
    }))
  } finally {
    detailExpandLoading.value = false
  }
}

const formatAnalysisDisplayValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '无'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const formatAnalysisJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const isJsonLikeString = (value: unknown): boolean => {
  if (typeof value !== 'string') return false
  const text = value.trim()
  if (text.length <= 200) return false
  return (text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))
}

const getAnalysisBasicInfo = (data: any): Array<{ key: string; value: string }> => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return []
  const excluded = new Set(['检测原始数据检测项目集合', 'Id', 'id', 'ISDELETED'])
  return Object.entries(data)
    .filter(([key, value]) => {
      if (excluded.has(key)) return false
      if (Array.isArray(value)) return false
      if (value !== null && typeof value === 'object') return false
      if (key.toUpperCase().includes('JSON')) return false
      if (isJsonLikeString(value)) return false
      return true
    })
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => ({ key, value: formatAnalysisDisplayValue(value) }))
}

const getAnalysisProjects = (data: any): any[] => {
  if (!data || typeof data !== 'object') return []
  const projects = data?.检测原始数据检测项目集合
  if (Array.isArray(projects) && projects.length > 0) return projects

  // 新详情接口直接返回单个"检测项目"平铺结构：格式描述、项目特殊属性、
  // 详细数据主表集合都与项目信息在同一层，将 data 自身包装为单个项目来复用渲染逻辑
  if (Array.isArray(data?.检测原始数据详细数据主表集合) && data.检测原始数据详细数据主表集合.length > 0) {
    return [data]
  }
  return []
}

const getAnalysisSpecialAttributes = (project: any): any[] => {
  const attrs = project?.检测原始数据检测项目特殊属性集合
  return Array.isArray(attrs) ? attrs : []
}

const getAnalysisMainTables = (project: any): any[] => {
  const tables = project?.检测原始数据详细数据主表集合
  return Array.isArray(tables) ? tables : []
}

const getAnalysisSingleAttributes = (mainData: any): any[] => {
  const attrs = mainData?.检测原始数据检测项目单条数据特殊属性集合
  return Array.isArray(attrs) ? attrs : []
}

const getAnalysisSubRows = (mainData: any): any[] => {
  const rows = mainData?.检测原始数据详细数据子表集合
  return Array.isArray(rows) ? rows : []
}

const getAnalysisTableColumns = (project: any, mainData: any): Array<{ prop: string; label: string }> => {
  const titles = project?.检测原始数据检测数据格式描述?.横向标题
  if (Array.isArray(titles) && titles.length > 0) {
    return [
      { prop: 'Header', label: '行号' },
      ...titles.map((title: unknown, index: number) => ({
        prop: `_${index + 1}`,
        label: formatAnalysisDisplayValue(title)
      }))
    ]
  }

  const firstRow = getAnalysisSubRows(mainData)[0]
  if (!firstRow || typeof firstRow !== 'object') return []
  return Object.keys(firstRow)
    .filter(key => key !== 'Id' && key !== 'id')
    .map(key => ({ prop: key, label: key }))
}

interface AnalysisRemarkImage {
  fileName: string
  remark: string
  url: string
  loading: boolean
  error: boolean
}

// 各主表现场照片缓存，key 为主表 Id
const analysisRemarkImagesByMainTable = ref<Map<string, AnalysisRemarkImage[]>>(new Map())

const clearAnalysisRemarkImages = () => {
  const map = analysisRemarkImagesByMainTable.value
  for (const images of map.values()) {
    for (const image of images) {
      if (image.url?.startsWith('blob:')) window.URL.revokeObjectURL(image.url)
    }
  }
  analysisRemarkImagesByMainTable.value = new Map()
}

const toAnalysisRemarkText = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  const text = String(value).trim()
  if (!text || text === 'null' || text === 'undefined') return ''
  return text
}

// 兼容 [文件名, 备注] / 字符串 / {文件名, 备注} 等来源
const parseAnalysisRemarkImageList = (source: unknown): Array<{ fileName: string; remark: string }> => {
  if (typeof source === 'string') return parseAnalysisRemarkImageList(parseAnalysisDetailValue(source))
  if (!Array.isArray(source)) return []
  const result: Array<{ fileName: string; remark: string }> = []
  for (const item of source) {
    if (typeof item === 'string') {
      const fileName = toAnalysisRemarkText(item)
      if (fileName) result.push({ fileName, remark: '' })
      continue
    }
    if (Array.isArray(item)) {
      const fileName = toAnalysisRemarkText(item[0])
      if (fileName) result.push({ fileName, remark: toAnalysisRemarkText(item[1]) })
      continue
    }
    if (item && typeof item === 'object') {
      const record = item as Record<string, unknown>
      const fileName = toAnalysisRemarkText(
        record['文件名'] ?? record['fileName'] ?? record['名称'] ?? record['name'] ?? record['图片']
      )
      if (fileName) {
        result.push({
          fileName,
          remark: toAnalysisRemarkText(record['备注'] ?? record['remark'] ?? record['说明'])
        })
      }
    }
  }
  return result
}

const getAnalysisRemarkImageSourceList = (mainData: any): Array<{ fileName: string; remark: string }> => {
  if (!mainData || typeof mainData !== 'object') return []
  const jsonSource = parseAnalysisRemarkImageList(mainData['备注图像列表json'])
  if (jsonSource.length > 0) return jsonSource
  return parseAnalysisRemarkImageList(mainData['备注图像列表'])
}

const updateAnalysisRemarkImage = (mainTableId: string, fileName: string, patch: Partial<AnalysisRemarkImage>) => {
  const next = new Map(analysisRemarkImagesByMainTable.value)
  const images = next.get(mainTableId) ?? []
  next.set(mainTableId, images.map(image => (image.fileName === fileName ? { ...image, ...patch } : image)))
  analysisRemarkImagesByMainTable.value = next
}

// 幂等加载单个主表现场照片，已有加载记录时跳过，避免重复请求
const loadAnalysisRemarkImages = async (mainData: any): Promise<void> => {
  const mainTableId = String(mainData?.Id || mainData?.id || '')
  if (!mainTableId) return
  const sourceImages = getAnalysisRemarkImageSourceList(mainData)
  if (sourceImages.length === 0) return
  if ((analysisRemarkImagesByMainTable.value.get(mainTableId) ?? []).length > 0) return

  const next = new Map(analysisRemarkImagesByMainTable.value)
  next.set(mainTableId, sourceImages.map(({ fileName, remark }) => ({
    fileName,
    remark,
    url: '',
    loading: true,
    error: false
  })))
  analysisRemarkImagesByMainTable.value = next

  for (const { fileName } of sourceImages) {
    try {
      const blob = await list.downloadPhoto({ 文件名: fileName })
      const url = window.URL.createObjectURL(blob)
      updateAnalysisRemarkImage(mainTableId, fileName, { url, loading: false, error: false })
    } catch (error) {
      console.error('加载现场照片失败:', fileName, error)
      updateAnalysisRemarkImage(mainTableId, fileName, { url: '', loading: false, error: true })
    }
  }
}

// 遍历详情数据中所有主表（兼容平铺与项目集合两种结构）加载现场照片
const loadAllAnalysisRemarkImages = (data: any): void => {
  if (!data || typeof data !== 'object') return
  for (const project of getAnalysisProjects(data)) {
    for (const mainData of getAnalysisMainTables(project)) {
      loadAnalysisRemarkImages(mainData)
    }
  }
}

const getAnalysisImages = (mainData: any): AnalysisRemarkImage[] => {
  const mainTableId = String(mainData?.Id || mainData?.id || '')
  if (!mainTableId) return []
  return analysisRemarkImagesByMainTable.value.get(mainTableId) ?? []
}

const getAnalysisImageInitialIndex = (mainData: any, imageIndex: number): number => {
  const images = getAnalysisImages(mainData)
  let previewIndex = 0
  for (let i = 0; i < images.length; i += 1) {
    if (!images[i].url) continue
    if (i === imageIndex) return previewIndex
    previewIndex += 1
  }
  return 0
}

// 页面销毁时释放已下载的图片 blob 地址
onBeforeUnmount(() => {
  clearAnalysisRemarkImages()
})

// 读取详情中的状态字段，兼容中英文字段名
const getBusinessStatus = (detail: Record<string, unknown> | null): string => {
  if (!detail) return ''
  const statusValue =
    detail['状态'] ??
    detail.status ??
    detail.Status

  return typeof statusValue === 'string' ? statusValue : String(statusValue ?? '')
}

// 统一状态文本，避免"配置完成，执行中/配置完成执行中"这类标点差异导致判断失败
const normalizeBusinessStatus = (status: string): string => {
  return status.replace(/[，,\s]/g, '')
}

// 当前经营项目是否包含检测原始数据
const hasDetectionData = computed(() => detectionDataList.value.length > 0)

// 是否显示经营项目按钮区域：仅详情点击后 + 有选中项目 + 当前项目允许编辑 + 有检测原始数据
const showBusinessActionArea = computed(() => {
  return !!selectedBusinessId.value
    && businessDetailClickedId.value === selectedBusinessId.value
    && currentBusinessEditable.value
    && hasDetectionData.value
})

// 状态为"执行完成"时显示撤回按钮
const showWithdrawButton = computed(() => {
  return showBusinessActionArea.value
    && normalizeBusinessStatus(getBusinessStatus(currentBusinessDetail.value)) === normalizeBusinessStatus(STATUS_EXECUTION_COMPLETED)
})

// 状态为"配置完成，执行中"时显示完成按钮
const showCompleteButton = computed(() => {
  return showBusinessActionArea.value
    && normalizeBusinessStatus(getBusinessStatus(currentBusinessDetail.value)) === normalizeBusinessStatus(STATUS_CONFIG_EXECUTING)
})

// 加载经营项目列表
const loadBusinessProjects = async () => {
  try {
    leftLoading.value = true
    console.log('发送请求参数:', { 
      page: leftPagination.value.currentPage, 
      intPageSize: leftPagination.value.pageSize 
    })
    console.log('筛选和排序参数:', filterParams.value)
    
    const res = await ParameterSettings.analysisOfBusinessListIndex(
      { 
        page: leftPagination.value.currentPage, 
        intPageSize: leftPagination.value.pageSize 
      },
      filterParams.value // 筛选和排序参数
    )

    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      businessProjects.value = res.response.data
      leftPagination.value.total = res.response.dataCount || res.response.data.length
      
      // 从接口返回中提取model数据
      if (res.response.model && typeof res.response.model === 'object') {
        tableModel.value = res.response.model
      } else {
        tableModel.value = undefined
      }
      
      ElMessage.success(`加载成功，共 ${res.response.dataCount} 个项目`)
    } else {
      ElMessage.error('加载经营项目列表失败')
      tableModel.value = undefined
    }
  } catch (error) {
    console.error('加载经营项目列表失败:', error)
    ElMessage.error('加载经营项目列表失败')
  } finally {
    leftLoading.value = false
  }
}
 
// 筛选处理函数
const handleFilter = (filters: any[]) => {
  console.log('筛选和排序参数:', filters)
  filterParams.value = filters
  leftPagination.value.currentPage = 1 // 重置到第一页
  loadBusinessProjects()
}

// 排序处理函数
const handleSort = (column: string, type: number) => {
  console.log('排序:', column, type === 1 ? '升序' : '降序')
  // 排序参数会通过filter事件一起传递，这里不需要额外处理
}

// 查看经营项目详情（点击详情按钮）
const handleBusinessDetail = async (row: any) => {
  try {
    rightLoading.value = true
    businessDetailClickedId.value = row.Id
    selectedBusinessId.value = row.Id
    currentBusinessEditable.value = false
    
    const res = await ParameterSettings.analysisOfBusinessById({ id: row.Id })

    if (res && (res.status === 0 || res.status === 200) && res.success && res.response) {
      // 详情接口返回顶层 editable，控制下方经营项目按钮区显隐
      currentBusinessEditable.value = Boolean(res.editable)
      currentBusinessDetail.value = res.response
      currentBusinessName.value = res.response.名称 || '未命名项目'
      
      // 提取检测原始数据主表集合
      if (res.response.检测原始数据主表集合 && Array.isArray(res.response.检测原始数据主表集合) && res.response.检测原始数据主表集合.length > 0) {
        detectionDataList.value = res.response.检测原始数据主表集合
      } else {
        detectionDataList.value = []
      }
      
      // 加载筛选选项数据
      await loadFilterOptions()
      // 加载分析数据
      await loadAnalysisData()
    } else {
      currentBusinessEditable.value = false
      ElMessage.error('加载项目详情失败') 
      detectionDataList.value = []
    }
  } catch (error) {
    currentBusinessEditable.value = false
    console.error('加载项目详情失败:', error)
    ElMessage.error('加载项目详情失败')
    detectionDataList.value = []
  } finally {
    rightLoading.value = false
  }
}

// 加载筛选选项数据
const loadFilterOptions = async () => {
  if (!selectedBusinessId.value) return
  
  try {
    const res = await ParameterSettings.analysisOfBusinessById({ id: selectedBusinessId.value })
    
    if (res && (res.status === 0 || res.status === 200) && res.success && res.response) {
      const response = res.response
      
      // 从建筑列表提取层数和建筑名称
      const buildingList = response.建筑列表 || []
      const floors = new Set<string>()
      const buildingNames = new Set<string>()
      
      buildingList.forEach((building: any) => {
        if (building.层数) floors.add(String(building.层数))
        if (building.名称) buildingNames.add(building.名称)
      })
      
      filterOptions.value.层数 = Array.from(floors).sort((a, b) => Number(a) - Number(b))
      filterOptions.value.建筑名称 = Array.from(buildingNames)
      
      // 从检测项目列表提取检测项目名称（去重）
      const projectList = response.检测项目列表 || []
      const projectNames = new Set<string>()
      projectList.forEach((p: any) => {
        if (p.名称) projectNames.add(p.名称)
      })
      filterOptions.value.检测项目名称 = Array.from(projectNames)
      
      // 从检测原始数据主表集合提取检测人员
      const rawDataList = response.检测原始数据主表集合 || []
      const staffSet = new Set<string>()
      rawDataList.forEach((data: any) => {
        if (data.提交人员) staffSet.add(data.提交人员)
        if (data.检验人员) staffSet.add(data.检验人员)
      })
      filterOptions.value.检测人员 = Array.from(staffSet)
      
      // 检测对象名称需要从检测项目中获取
      const objectTypes = new Set<string>()
      projectList.forEach((p: any) => {
        if (p.检测对象类型) objectTypes.add(p.检测对象类型)
      })
      filterOptions.value.检测对象名称 = Array.from(objectTypes)
    }
  } catch (error) {
    console.error('加载筛选选项失败:', error)
  }
}

// 分组条件变化处理（确保至少选中一项）
const handleGroupConditionChange = (changedField: string) => {
  const selectedCount = [
    analysisFilters.value.人员,
    analysisFilters.value.检测项目,
    analysisFilters.value.检测对象,
    analysisFilters.value.建筑,
    analysisFilters.value.楼层
  ].filter(Boolean).length
  
  // 如果取消选择后没有任何选中项，阻止取消并提示
  if (selectedCount === 0) {
    ElMessage.warning('至少需要选中一个分组条件')
    // 恢复刚才取消的选项
    if (changedField === '人员') analysisFilters.value.人员 = true
    else if (changedField === '检测项目') analysisFilters.value.检测项目 = true
    else if (changedField === '检测对象') analysisFilters.value.检测对象 = true
    else if (changedField === '建筑') analysisFilters.value.建筑 = true
    else if (changedField === '楼层') analysisFilters.value.楼层 = true
    return
  }
  
  // 正常触发数据加载
  handleAnalysisFilterChange()
}

const formatAnalysisDateTime = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

const setAnalysisDateRange = (range: 'today' | 'yesterday') => {
  const targetDate = new Date()
  if (range === 'yesterday') {
    targetDate.setDate(targetDate.getDate() - 1)
  }

  const startDate = new Date(targetDate)
  startDate.setHours(0, 0, 0, 0)
  const endDate = new Date(targetDate)
  endDate.setHours(23, 59, 59, 0)

  analysisFilters.value.开始时间 = formatAnalysisDateTime(startDate)
  analysisFilters.value.结束时间 = formatAnalysisDateTime(endDate)
  handleAnalysisFilterChange()
}

// 加载分析数据
const loadAnalysisData = async () => {
  if (!selectedBusinessId.value) return
  
  analysisLoading.value = true
  try {
    const params: any = {
      经营项目ID: selectedBusinessId.value,
      人员: analysisFilters.value.人员,
      检测项目: analysisFilters.value.检测项目,
      检测对象: analysisFilters.value.检测对象,
      建筑: analysisFilters.value.建筑,
      楼层: analysisFilters.value.楼层
    }
    
    // 添加可选筛选参数
    if (analysisFilters.value.层数) params.层数 = analysisFilters.value.层数
    if (analysisFilters.value.检测人员) params.检测人员 = analysisFilters.value.检测人员
    if (analysisFilters.value.检测对象名称) params.检测对象名称 = analysisFilters.value.检测对象名称
    if (analysisFilters.value.检测项目名称) params.检测项目名称 = analysisFilters.value.检测项目名称
    if (analysisFilters.value.建筑名称) params.建筑名称 = analysisFilters.value.建筑名称
    if (analysisFilters.value.开始时间) params.开始时间 = analysisFilters.value.开始时间
    if (analysisFilters.value.结束时间) params.结束时间 = analysisFilters.value.结束时间
    
    const res = await ParameterSettings.businessProgressAnalysis(params)
    
    if (res && (res.status === 0 || res.status === 200) && res.success && res.response) {
      analysisResult.value = res.response
    } else {
      analysisResult.value = []
    }
  } catch (error) {
    console.error('加载分析数据失败:', error)
    analysisResult.value = []
  } finally {
    analysisLoading.value = false
  }
}

// 筛选条件变化时重新加载数据
const handleAnalysisFilterChange = () => {
  loadAnalysisData()
}

// 显示详情弹框
const showDetailDialog = (row: any) => {
  // 打开新一轮详情前释放上一轮图片缓存
  clearAnalysisRemarkImages()
  currentDetailRow.value = row
  analysisDetailItems.value = getAnalysisDetailItems(row)
  detailExpandLoading.value = false
  detailDialogVisible.value = true
}

// 执行经营项目"撤回/完成"动作，统一处理接口调用与刷新逻辑
const executeBusinessAction = async (action: 'withdraw' | 'complete') => {
  if (!selectedBusinessId.value) {
    ElMessage.warning('请先选择经营项目')
    return
  }

  const request =
    action === 'withdraw'
      ? businessActionApi.analysisOfBusinessWithdraw
      : businessActionApi.analysisOfBusinessComplete
  const actionText = action === 'withdraw' ? BUSINESS_WITHDRAW_TEXT : BUSINESS_COMPLETE_TEXT

  businessActionLoading.value = true
  try {
    const res = await request({ id: selectedBusinessId.value })
    if (res && (res.status === 200 || res.status === 0) && res.success) {
      ElMessage.success(`${actionText}成功`)
      await handleBusinessDetail({ Id: selectedBusinessId.value })
      await loadBusinessProjects()
    } else {
      ElMessage.error(res?.msg || `${actionText}失败`)
    }
  } catch (error) {
    console.error(`${actionText}失败:`, error)
    ElMessage.error(`${actionText}失败`)
  } finally {
    businessActionLoading.value = false
  }
}

// 点击"经营项目撤回"
const handleBusinessWithdraw = async () => {
  await executeBusinessAction('withdraw')
}

// 点击"项目管理完成"
const handleBusinessComplete = async () => {
  await executeBusinessAction('complete')
}

const showPrintDialog = async (row: Record<string, unknown>) => {
  const rowId = row.Id as number
  if (!rowId) {
    ElMessage.warning('无法获取项目ID')
    return
  }

  try {
    // 如果不是当前选中的项目，先加载详情数据
    if (selectedBusinessId.value !== rowId) {
      rightLoading.value = true
      const res = await ParameterSettings.analysisOfBusinessById({ id: rowId })
      
      if (res && (res.status === 0 || res.status === 200) && res.success && res.response) {
        // 更新当前选中的项目信息
        selectedBusinessId.value = rowId
        currentBusinessDetail.value = res.response
        currentBusinessName.value = res.response.名称 || '未命名项目'
        
        // 检查是否有检测数据
        const hasData = res.response.检测原始数据主表集合 && 
                       Array.isArray(res.response.检测原始数据主表集合) && 
                       res.response.检测原始数据主表集合.length > 0
        
        if (!hasData) {
          detectionDataList.value = []
          ElMessage.warning('该经营项目模板下无检测原始数据')
          rightLoading.value = false
          return
        }
        
        // 更新右侧检测数据列表
        detectionDataList.value = res.response.检测原始数据主表集合
      } else {
        ElMessage.error('获取项目详情失败')
        rightLoading.value = false
        return
      }
      rightLoading.value = false
    } else {
      // 已经是当前选中项目，直接检查本地数据
      if (detectionDataList.value.length === 0) {
        ElMessage.warning('该经营项目模板下无检测原始数据')
        return
      }
    }

    // 有数据，直接调用打印列表
    handlePrintList(row)
  } catch (error) {
    console.error('检查项目数据失败:', error)
    ElMessage.error('检查项目数据失败')
    rightLoading.value = false
  }
}

// 打印列表 - 调用接口获取打印数据并跳转到打印页面（方向由数据中的字段决定）
const handlePrintList = async (row: Record<string, unknown>) => {
  const rowId = row.Id as number | string
  if (!rowId) {
    ElMessage.warning('无法获取项目ID')
    return
  }
  
  try {
    leftLoading.value = true
    const res = await ParameterSettings.operatingProjectPrint({ id: rowId })
    
    if (res && res.status === 200 && res.success && res.response) {
      // 将打印数据存储到 store 中，供打印页面使用
      // 接口返回的数据需要类型断言
      const printListData = res.response as Array<{
        方向: boolean; // true=横向，false=竖向
        记录编号: string;
        设备编号: string;
        环境温度: string;
        校核: string;
        记录: string;
        检验: string;
        计算: string;
        检验日期: string;
        检测原始数据检测项目: Record<string, unknown>;
      }>
      const cacheKey = `analysis-print-${String(rowId)}-${Date.now()}`
      detailStore.setPrintListData(cacheKey, printListData)
      
      // 跳转到打印列表页面（不传递方向参数，由数据决定）
      router.push({
        path: getMenuChildPathByComponent(
          '/RawDataManagement/AnalysisOfBusiness/index.vue',
          '原始记录打印',
          '/原始数据管理/经营项目数据分析/打印列表'
        ),
        query: {
          id: String(rowId),
          cacheKey
        }
      })
    } else {
      ElMessage.error(res?.msg || '获取打印数据失败')
    }
  } catch (error) {
    console.error('获取打印数据失败:', error)
  } finally {
    leftLoading.value = false
  }
}

// 刷新原始记录列表
const refreshDetectionData = async () => {
  if (!selectedBusinessId.value) return
  await handleBusinessDetail({ Id: selectedBusinessId.value })
}

// 查看检测数据详情
const viewDetectionDetail = (row: any) => {
  console.log('查看检测数据详情:', row)
  const analysisPath = getMenuPathByComponent(
    '/RawDataManagement/AnalysisOfBusiness/index.vue',
    '/原始数据管理/经营项目数据分析'
  )
  const detailPath = getMenuChildPathByComponent(
    '/RawDataManagement/OriginalDetectionList/index.vue',
    '原始检测数据详情',
    '/原始数据管理/原始检测数据列表/原始检测数据详情'
  )
  
  // 将参数存储到store中
  detailStore.setDetailParams(row.Id, analysisPath)
  
  // 保存当前选中的经营项目ID，用于返回时恢复状态
  detailStore.setAnalysisPageState(selectedBusinessId.value)
  
  // 跳转到详情页面
  router.push({
    path: detailPath,
    query: { id: row.Id }
  })
}

// 查看经营项目原始数据汇总
const viewBusinessDataSummary = () => {
  if (!selectedBusinessId.value) {
    ElMessage.warning('请先选择经营项目')
    return
  }
  
  if (detectionDataList.value.length === 0) {
    ElMessage.warning('该经营项目暂无检测数据')
    return
  }
  
  // 保存当前选中的经营项目ID和名称
  detailStore.setAnalysisPageState(selectedBusinessId.value)
  detailStore.setSummaryBusinessInfo({
    id: selectedBusinessId.value,
    name: currentBusinessName.value,
    code: currentBusinessDetail.value?.编码 || ''
  })
  
  // 跳转到汇总页面（只传经营项目ID）
  router.push({
    path: getMenuChildPathByComponent(
      '/RawDataManagement/AnalysisOfBusiness/index.vue',
      '原始数据汇总',
      '/原始数据管理/经营项目数据分析/原始数据汇总'
    ),
    query: { 
      id: String(selectedBusinessId.value)
    }
  })
}

// 判断人员是否已设置（检验人员、校核人员、计算人员全部填写才算已设置）
const isStaffSet = (item: Record<string, unknown>): boolean => {
  return !!(item.检验人员 && item.校核人员 && item.计算人员)
}

// 打开设置人员弹框
const openStaffDialog = (item: any) => {
  currentStaffItemId.value = item.Id
  // 初始化表单（如果有现有数据可以预填）
  staffForm.value = {
    检验人员: item.检验人员 || '',
    校核人员: item.校核人员 || '',
    计算人员: item.计算人员 || ''
  }
  staffDialogVisible.value = true
}

// 提交设置人员
const handleSetStaff = async () => {
  if (!currentStaffItemId.value) {
    ElMessage.warning('未选择检测数据')
    return
  }
  
  staffLoading.value = true
  try {
    const res = await ParameterSettings.setDetectionStaff({
      id: currentStaffItemId.value,
      检验人员: staffForm.value.检验人员,
      校核人员: staffForm.value.校核人员,
      计算人员: staffForm.value.计算人员
    })
    
    if (res && (res.status === 200 || res.success)) {
      ElMessage.success('设置人员成功')
      staffDialogVisible.value = false
      // 刷新当前经营项目的检测数据列表
      if (selectedBusinessId.value) {
        await handleBusinessDetail({ Id: selectedBusinessId.value })
      }
    } else {
      ElMessage.error(res?.msg || '设置人员失败')
    }
  } catch (error) {
    console.error('设置人员失败:', error)
    ElMessage.error('设置人员失败')
  } finally {
    staffLoading.value = false
  }
}

// 左侧分页处理
const handleLeftSizeChange = (val: number) => {
  leftPagination.value.pageSize = val
  leftPagination.value.currentPage = 1
  loadBusinessProjects()
}

const handleLeftPageChange = (val: number) => {
  leftPagination.value.currentPage = val
  loadBusinessProjects()
}

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return '未设置'
  const date = new Date(dateStr)
  return date.toLocaleString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 恢复选中状态
const restoreSelectedState = async () => {
  const savedBusinessId = detailStore.getAnalysisPageState()
  if (savedBusinessId) {
    // 找到对应的经营项目并加载详情
    const targetProject = businessProjects.value.find((item: any) => item.Id === savedBusinessId)
    if (targetProject) {
      await handleBusinessDetail(targetProject)
    } else {
      // 如果在当前页找不到，直接用ID加载
      await handleBusinessDetail({ Id: savedBusinessId })
    }
  }
}

// 格式化分组条件：字段映射 + 排序
const getFormattedGroupCondition = (groupCondition: Record<string, any>) => {
  if (!groupCondition) return {}
  
  // 字段映射规则
  const fieldMapping: Record<string, string> = {
    '员工姓名': '检测人员',
    '数据名称': '检测项目'
  }
  
  // 字段排序规则（按照要求的顺序）
  const fieldOrder = ['检测人员', '检测项目', '检测对象', '建筑名称', '层数']
  
  // 先进行字段映射
  const mappedCondition: Record<string, any> = {}
  Object.entries(groupCondition).forEach(([key, value]) => {
    const mappedKey = fieldMapping[key] || key
    mappedCondition[mappedKey] = value
  })
  
  // 按照指定顺序排序
  const sortedCondition: Record<string, any> = {}
  fieldOrder.forEach(field => {
    if (mappedCondition[field] !== undefined) {
      sortedCondition[field] = mappedCondition[field]
    }
  })
  
  // 添加未在排序列表中的其他字段
  Object.entries(mappedCondition).forEach(([key, value]) => {
    if (!fieldOrder.includes(key)) {
      sortedCondition[key] = value
    }
  })
  
  return sortedCondition
}

// 组件挂载时加载数据
onMounted(async () => {
  await loadBusinessProjects()
  // 加载完列表后恢复选中状态（仅在从详情页返回时）
  await restoreSelectedState()
})
</script>

<style scoped>
.analysis-of-business {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.main-content {
  display: flex;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

/* 左侧面板 */
.left-panel {
  flex: 0 0 40%;
  max-width: 40%;
}

.list-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.list-card :deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}


.business-list-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.business-list-container :deep(.dynamic-table-card) {
  box-shadow: none;
  border: none;
}

/* 右侧面板 */
.right-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
}

.detection-list-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: none;
  background-color: #fff;
}

.detection-list-card :deep(.el-card__body) {
  flex: 1;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.detection-list-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 16px;
}

.detection-scrollbar {
  flex: 1;
  min-height: 0;
}

.detection-item {
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  transition: all 0.3s;
  background-color: #fff;
}

.detection-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border-color: #dcdfe6;
  transform: translateY(-2px);
}

.detection-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.detection-info {
  flex: 1;
  min-width: 0;
}

.detection-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.detection-info h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
}

.detection-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #909399;
}

.detection-time {
  display: flex;
  align-items: center;
  gap: 4px;
}

.detection-projects {
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  margin-top: 16px;
}

.project-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.project-tag {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 分析卡片 */
.business-action-bar {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.business-action-bar--inside {
  flex-shrink: 0;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid #ebeef5;
}

.analysis-card {
  flex: 1;
  min-height: 0;
  border: none;
  display: flex;
  flex-direction: column;
}

.analysis-card :deep(.el-card__body) {
  flex: 1;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.analysis-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: #fcfcfc;
  overflow: hidden;
}

.analysis-container :deep(.el-empty) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px 16px;
  box-sizing: border-box;
}

.analysis-container :deep(.el-empty__image) {
  margin-bottom: 12px;
}

.analysis-filters {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.filter-row {
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  align-items: center;
  overflow-x: auto;
}

.filter-label {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
  white-space: nowrap;
  margin-right: 4px;
  flex-shrink: 0;
}

.filter-row .el-select {
  flex: 0 0 auto;
  width: 130px;
  min-width: 130px;
}

.filter-row .el-select:first-of-type {
  width: 90px;
  min-width: 90px;
}

.filter-row .floor-select {
  width: 65px !important;
  min-width: 65px !important;
}

.filter-row .analysis-time-picker {
  flex: 0 0 auto;
  width: 180px;
}

.radio-row {
  padding: 8px 0;
  border-top: 1px solid #e4e7ed;
  border-bottom: 1px solid #e4e7ed;
}

.total-count {
  margin-left: auto;
  margin-right: 16px;
  color: #606266;
  font-size: 14px;
}

.total-count strong {
  color: #409eff;
  font-size: 16px;
  margin-left: 4px;
}

.analysis-result {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 80px;
  overflow: hidden;
  margin-top: 8px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}

.analysis-table {
  flex: 1;
  min-height: 0;
}

/* 让 el-table 基于容器高度产生内部滚动 */
.analysis-result :deep(.el-table) {
  height: 100% !important;
}

.analysis-result :deep(.el-table__inner-wrapper) {
  height: 100%;
}

.analysis-result :deep(.el-table__body-wrapper) {
  overflow-y: auto !important;
}

.analysis-result :deep(.el-scrollbar__wrap) {
  overflow-y: auto !important;
}

/* 添加自定义滚动条样式 */
.analysis-result::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.analysis-result::-webkit-scrollbar-thumb {
  background-color: #c0c4cc;
  border-radius: 3px;
}

.analysis-result::-webkit-scrollbar-track {
  background-color: #f5f7fa;
}

.group-info {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: center;
}

.group-item {
  display: flex;
  gap: 4px;
  font-size: 13px;
  line-height: 1.5;
  white-space: nowrap;
}

.group-label {
  color: #909399;
  font-weight: 500;
}

.group-value {
  color: #303133;
  font-weight: 600;
}

/* 详情弹框样式 */
.detail-dialog-content {
  max-height: 78vh;
  overflow: auto;
  padding-right: 4px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 16px;
}

.detail-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.detail-item {
  padding: 10px 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  word-break: break-word;
  min-width: 0;
}

.detail-item--expanded {
  background-color: #f8fbff;
  border: 1px solid #d9ecff;
}

.detail-item-summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.detail-item-text {
  flex: 1;
  min-width: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.detail-item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  white-space: nowrap;
}

.detail-item-id {
  color: #909399;
  font-size: 12px;
}

.embedded-detail {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

.embedded-basic-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.embedded-info-tag {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin-right: 0;
}

.embedded-info-tag-label {
  flex-shrink: 0;
  color: #606266;
}

.embedded-info-tag-value {
  min-width: 0;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #303133;
}

.embedded-detail,
.embedded-project,
.embedded-main-table {
  min-width: 0;
}

.embedded-project-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.embedded-project {
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.embedded-section-title,
.embedded-table-title {
  color: #303133;
  font-weight: 600;
}

.embedded-section-title {
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.embedded-table-title {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: baseline;
  margin: 12px 0 8px;
  font-size: 13px;
}

.embedded-table-title span {
  color: #909399;
  font-weight: 400;
  overflow-wrap: anywhere;
}

.embedded-attributes,
.embedded-images {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
}

.embedded-images-label {
  color: #909399;
  font-size: 12px;
}

.embedded-image-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  max-width: 96px;
}

.embedded-image-thumb {
  width: 72px;
  height: 72px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #f5f7fa;
  cursor: zoom-in;
  overflow: hidden;
}

.embedded-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  box-sizing: border-box;
  border: 1px dashed #e4e7ed;
  border-radius: 4px;
  color: #909399;
  font-size: 12px;
  background: #fafafa;
}

.embedded-image-placeholder.is-error {
  color: #f56c6c;
  border-color: #fde2e2;
  background: #fef0f0;
}

.embedded-image-remark {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
}

.embedded-data-table {
  width: 100%;
  margin-top: 8px;
}

.embedded-json {
  max-height: 360px;
  margin: 0;
  padding: 12px;
  overflow: auto;
  color: #606266;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.analysis-detail-dialog :deep(.el-dialog__body) {
  padding-top: 12px;
  padding-bottom: 16px;
}

@media (max-width: 768px) {
  .detail-header,
  .detail-item-summary {
    flex-direction: column;
  }

  .detail-header-actions,
  .detail-item-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

.analysis-placeholder {
  text-align: center;
  margin-top: 10px;
  color: #606266;
  line-height: 1.4;
  font-size: 13px;
}

.analysis-placeholder p {
  margin: 4px 0;
}

.analysis-title {
  font-weight: bold;
  font-size: 14px;
  color: #303133;
  margin-bottom: 8px !important;
}

/* 响应式 */
@media (max-width: 1200px) {
  .analysis-of-business {
    height: auto;
    min-height: 100%;
    overflow: visible;
  }

  .main-content {
    flex-direction: column;
    height: auto;
  }

  .left-panel {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
    height: auto;
  }

  .right-panel {
    width: 100%;
    height: auto;
    overflow: visible;
    min-width: 0;
  }

  .list-card {
    height: auto;
    min-height: 420px;
  }

  .detection-list-card {
    height: auto;
    min-height: 420px;
  }
}

@media (max-height: 820px) {
  .analysis-of-business {
    height: auto;
    min-height: 100%;
    overflow: visible;
  }

  .main-content {
    height: auto;
  }

  .right-panel {
    overflow: visible;
  }
}

@media (max-width: 992px) {
  .detection-item-header {
    flex-direction: column;
    gap: 12px;
  }

  .detection-actions {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
}
</style>
